import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { useSelector } from 'react-redux';
import { RefObject, useEffect } from 'react';
import { ReactFlowInstance } from '@xyflow/react';

const getLastAdded = (state: any) => {
  const { sources, dataSets, sinks, entities } = state;
  const { lastAddedId: lastAddedSourceId } = sources;
  const { lastAddedId: lastAddedDataSetId } = dataSets;
  const { lastAddedId: lastAddedSinkId } = sinks;
  const lastAddedDataSet = entities.dataSets[lastAddedDataSetId];
  const lastAddedSink = entities.sinks[lastAddedSinkId];
  const lastAddedSource = entities.sources[lastAddedSourceId];

  return { lastAddedSink, lastAddedDataSet, lastAddedSource };
};

const checkLastAdded = (a, b) => {
  if (a.lastAddedSink?.flow_node_id !== b.lastAddedSink?.flow_node_id) return false;
  if (a.lastAddedDataSet?.flow_node_id !== b.lastAddedDataSet?.flow_node_id) return false;
  if (a.lastAddedSource?.flow_node_id !== b.lastAddedSource?.flow_node_id) return false;
  return true;
};

const defaultIgnore = {
  source: false,
  sink: false,
  nexset: false,
};

type TypeIgnore = {
  source?: boolean;
  sink?: boolean;
  nexset?: boolean;
};

type FitNodeOptions = {
  // when using 'mount' it will be 'mount' AND 'update'
  when?: 'update' | 'mount';
  ignore?: TypeIgnore;
};

export default function useFitNodeCreation(
  flowNodesRef: RefObject<ReactFlowInstance>,
  options: FitNodeOptions = { when: 'update', ignore: defaultIgnore },
) {
  const { when = 'update', ignore = defaultIgnore } = options;
  const { lastAddedSource, lastAddedSink, lastAddedDataSet } = useSelector(
    getLastAdded,
    checkLastAdded,
  );

  const useHook = when === 'update' ? useDidUpdateEffect : useEffect;

  const fitNodeAfterCreation = (flowNodeId: number, type: 'flow' | 'node' = 'node') => {
    setTimeout(() => {
      if (!flowNodesRef.current) return;
      const node = flowNodesRef.current.getNode(`${type}-${flowNodeId}`);
      if (node) {
        flowNodesRef.current.fitView({
          nodes: [node],
          duration: 500,
        });
      }
    }, 1000);
  };

  useHook(() => {
    if (lastAddedSink?.flow_node_id && !ignore.sink) {
      fitNodeAfterCreation(lastAddedSink.flow_node_id);
    }
  }, [lastAddedSink?.flow_node_id]);

  useHook(() => {
    if (lastAddedSource?.flow_node_id && !ignore.source) {
      fitNodeAfterCreation(lastAddedSource.origin_node_id, 'flow');
    }
  }, [lastAddedSource?.flow_node_id]);

  useHook(() => {
    if (lastAddedDataSet?.flow_node_id && !ignore.nexset) {
      fitNodeAfterCreation(lastAddedDataSet?.flow_node_id);
    }
  }, [lastAddedDataSet?.flow_node_id]);
}
